/**
 * Money — the single chokepoint for INV-1.
 *
 * Ledger amounts are integer cents; split amounts are integer minor units of
 * their own currency, and FX rates are integer nanos. This module is
 * the ONLY place that converts between decimal strings and money, and the only
 * place permitted to produce a formatted currency string.
 *
 * `scripts/check-invariants.sh` enforces the full INV-1 rule list in
 * `.claude/docs/CODING_STANDARDS.md`: decimal parsing, BigInt/scaling and
 * fraction-dropping formatters stay here under `src/` and `shared/`.
 *
 * Note what this file does NOT do: it never multiplies a parsed float by 100.
 * `Math.round(parseFloat("8.115") * 100)` is 811 on some inputs and 812 on others,
 * because the float was already wrong before the multiply. Digits are parsed as
 * integers and combined, so the conversion is exact for every input.
 */

import { exponentOf, isCurrencyCode, MAX_MINOR_INPUT } from "./currencies";

declare const CentsBrand: unique symbol;

/**
 * An integer number of cents. The brand exists so a bare `number` cannot be
 * passed where money is expected — a compile-time echo of INV-1.
 */
export type Cents = number & { readonly [CentsBrand]: true };

/** $999,999.99 — a sane ceiling that still fits any plausible personal expense. */
export const MAX_AMOUNT_CENTS = 99_999_999;

declare const MinorBrand: unique symbol;
declare const RateNanoBrand: unique symbol;

/** Safe integer minor units of the currency carried alongside the amount. */
export type Minor = number & { readonly [MinorBrand]: true };
/** One major unit of `from` buys rateNano / 1e9 major units of `to`. */
export type RateNano = number & { readonly [RateNanoBrand]: true };

const DECIMAL_RADIX = 10n;
const MINOR_SCALES = {
  0: DECIMAL_RADIX ** BigInt(0),
  2: DECIMAL_RADIX ** BigInt(2),
  3: DECIMAL_RADIX ** BigInt(3),
} as const;
const RATE_DECIMALS = 9;
const RATE_SCALE = DECIMAL_RADIX ** BigInt(RATE_DECIMALS);
export const MAX_RATE_NANO = 1_000_000_000_000_000;
const BASIS_POINTS = 10_000n;
const MINOR_DIGITS_REMEDY = "Enter digits only, without a currency symbol or spaces — for example 1,234.56";
const minorFormatters = new Map<string, { amount: Intl.NumberFormat; fraction: Intl.NumberFormat }>();

/** Construct signed Minor from a persisted or computed integer (INV-7). */
export function minor(value: number): Minor {
  if (!Number.isSafeInteger(value)) {
    throw new Error(`Money must be a safe integer of minor units, received ${String(value)}`);
  }
  return (value === 0 ? 0 : value) as Minor;
}

/** Construct a persisted or computed positive integer FX rate (INV-7). */
export function rateNano(value: number): RateNano {
  if (!Number.isSafeInteger(value) || value < 1 || value > MAX_RATE_NANO) {
    throw new Error(`FX rate must be a safe integer from 1 to ${String(MAX_RATE_NANO)} nanos`);
  }
  return value as RateNano;
}

/** Digit-wise decimal parsing with bounded integer accumulation, never a float. */
function parseDecimal(
  input: string, decimals: number, maximum: bigint, label: string, maximumError?: () => string,
): bigint {
  if (input === "") throw new Error(`${label} is empty`);
  if (!/^\d*\.?\d*$/.test(input) || input === ".") {
    throw new Error(`Not a valid ${label}: ${input}`);
  }
  const [whole = "", fraction = ""] = input.split(".");
  if (fraction.length > decimals) {
    throw new Error(`${label} carries at most ${String(decimals)} decimal places: ${input}`);
  }
  let value = 0n;
  for (const digit of whole + fraction.padEnd(decimals, "0")) {
    value = value * DECIMAL_RADIX + BigInt(digit);
    if (value > maximum) throw new Error(maximumError?.() ?? `${label} exceeds the maximum permitted value`);
  }
  return value;
}

/** Nonnegative plain decimal input, with optional correctly grouped thousands commas. */
export function parseMinor(input: string, currency: string): Minor {
  const exponent = exponentOf(currency);
  const trimmed = input.trim();
  if (trimmed.includes(",") && !/^\d{1,3}(,\d{3})*(\.\d*)?$/.test(trimmed)) {
    throw new Error(MINOR_DIGITS_REMEDY);
  }
  const cleaned = trimmed.replace(/,/g, "");
  if (cleaned !== "" && (!/^\d*\.?\d*$/.test(cleaned) || cleaned === ".")) throw new Error(MINOR_DIGITS_REMEDY);
  return minor(Number(parseDecimal(cleaned, exponent, BigInt(MAX_MINOR_INPUT), "Amount",
    () => `Amount exceeds the maximum of ${formatMinor(minor(MAX_MINOR_INPUT), currency)}`)));
}

/** Positive plain decimal rate, 1..1e15 nanos inclusive; at most nine decimals. */
export function parseRate(input: string): RateNano {
  const value = parseDecimal(input.trim(), RATE_DECIMALS, BigInt(MAX_RATE_NANO), "FX rate");
  if (value === 0n) throw new Error("FX rate must be positive, at least one nano");
  return rateNano(Number(value));
}

/** Editable decimal rate with every nano preserved. */
export function toRateInputString(value: RateNano): string {
  const digits = String(rateNano(value)).padStart(RATE_DECIMALS + 1, "0");
  const whole = digits.slice(0, -RATE_DECIMALS);
  const fraction = digits.slice(-RATE_DECIMALS).replace(/0+$/, "");
  return fraction === "" ? whole : `${whole}.${fraction}`;
}

/** Machine-readable exact minor-unit amount, including signed balances. */
export function toMinorInputString(value: Minor, currency: string): string {
  const exponent = exponentOf(currency);
  const scale = MINOR_SCALES[exponent];
  const magnitude = BigInt(Math.abs(minor(value)));
  const sign = value < 0 ? "-" : "";
  const whole = String(magnitude / scale);
  return exponent === 0 ? `${sign}${whole}`
    : `${sign}${whole}.${String(magnitude % scale).padStart(exponent, "0")}`;
}

/** Locale formatting at the table's precision, without losing a minor unit to floats. */
export function formatMinor(value: Minor, currency: string, locale = "en-SG"): string {
  const exponent = exponentOf(currency);
  const scale = MINOR_SCALES[exponent];
  const magnitude = BigInt(Math.abs(minor(value)));
  const whole = magnitude / scale;
  // BigInt has no negative zero; Number -0 preserves the sign of a sub-major balance.
  const signedWhole = value < 0 ? (whole === 0n ? -0 : -whole) : whole;
  const key = JSON.stringify([locale, currency]);
  let formatters = minorFormatters.get(key);
  if (!formatters) {
    formatters = {
      amount: new Intl.NumberFormat(locale, {
        style: "currency", currency, currencyDisplay: "symbol",
        minimumFractionDigits: exponent, maximumFractionDigits: exponent,
      }),
      fraction: new Intl.NumberFormat(locale, {
        useGrouping: false, minimumIntegerDigits: exponent || 1,
      }),
    };
    minorFormatters.set(key, formatters);
  }
  const fraction = formatters.fraction.format(magnitude % scale);
  return formatters.amount.formatToParts(signedWhole).map(part => {
    if (part.type === "fraction") return fraction;
    if (part.type === "minusSign") return "−";
    return part.value;
  }).join("");
}

/** FX rounds to nearest target minor unit; exact halves round away from zero in both signs. */
export function convertMinor(amount: Minor, from: string, to: string, rate: RateNano): Minor {
  const sourceScale = MINOR_SCALES[exponentOf(from)];
  const targetScale = MINOR_SCALES[exponentOf(to)];
  minor(amount);
  rateNano(rate);
  // Same-currency conversion is identity only at rate 1; refuse contradictory input.
  if (from === to) {
    if (BigInt(rate) !== RATE_SCALE) throw new Error("A same-currency FX rate must equal 1");
    return minor(amount);
  }
  const numerator = BigInt(Math.abs(amount)) * BigInt(rate) * targetScale;
  const denominator = sourceScale * RATE_SCALE;
  const rounded = numerator / denominator + (numerator % denominator * 2n >= denominator ? 1n : 0n);
  return minor(Number(amount < 0 ? -rounded : rounded));
}

/** Exact proportional shares: largest remainders first, ties by lowest index; negatives mirror positives. */
export function distributeLargestRemainder(total: Minor, weights: readonly number[]): Minor[] {
  minor(total);
  const integerWeights = weights.map(weight => {
    if (!Number.isSafeInteger(weight) || weight < 0) throw new Error("Split weights must be non-negative safe integers");
    return BigInt(weight);
  });
  const sum = integerWeights.reduce((a, b) => a + b, 0n);
  if (sum === 0n) throw new Error("Split weights must sum to a positive integer");
  const magnitude = BigInt(Math.abs(total));
  const rows = integerWeights.map((weight, index) => ({
    index, units: magnitude * weight / sum, remainder: magnitude * weight % sum,
  }));
  let residual = magnitude - rows.reduce((acc, row) => acc + row.units, 0n);
  const ranked = [...rows].sort((a, b) => a.remainder === b.remainder ? a.index - b.index
    : a.remainder > b.remainder ? -1 : 1);
  for (const row of ranked) {
    if (residual === 0n) break;
    row.units += 1n;
    residual -= 1n;
  }
  return rows.map(row => minor(Number(total < 0 ? -row.units : row.units)));
}

export function centsAsMinor(value: Cents, currency: string): Minor {
  if (exponentOf(currency) !== 2) throw new Error("Cents require a two-decimal currency");
  return minor(value);
}

export function minorAsCents(value: Minor, currency: string): Cents {
  if (exponentOf(currency) !== 2) throw new Error("Cents require a two-decimal currency");
  return cents(minor(value));
}

/** Nonnegative part / (part + other), in basis points rounded down, for salary display. */
export function ratioBasisPoints(part: Cents, other: Cents): number {
  const positivePart = BigInt(Math.max(0, minor(part)));
  const positiveOther = BigInt(Math.max(0, minor(other)));
  const total = positivePart + positiveOther;
  return total === 0n ? 0 : Number(positivePart * BASIS_POINTS / total);
}

/** Construct `Cents` from a value already known to be whole. Throws otherwise (INV-7). */
export function cents(value: number): Cents {
  if (!Number.isInteger(value)) {
    throw new Error(`Money must be whole cents, received ${String(value)}`);
  }
  return value as Cents;
}

export const ZERO = cents(0);

/**
 * Parse user input into cents. The only decimal→cents conversion in the app.
 *
 * Accepts `"12"`, `"12."`, `"12.3"`, `"12.34"`, `".5"`, with optional spaces,
 * a leading currency symbol, and thousands separators. Rejects anything else
 * loudly rather than guessing (INV-7).
 */
export function parseAmount(input: string): Cents {
  const cleaned = input.trim().replace(/[$\s,]/g, "");

  if (cleaned === "") {
    throw new Error("Amount is empty");
  }
  if (!/^\d*\.?\d*$/.test(cleaned) || cleaned === ".") {
    throw new Error(`Not a valid amount: ${input}`);
  }

  const dot = cleaned.indexOf(".");
  const wholePart = dot === -1 ? cleaned : cleaned.slice(0, dot);
  const fracPart = dot === -1 ? "" : cleaned.slice(dot + 1);

  if (fracPart.length > 2) {
    throw new Error(`Amounts carry at most 2 decimal places: ${input}`);
  }

  const whole = wholePart === "" ? 0 : Number.parseInt(wholePart, 10);
  const frac = fracPart === "" ? 0 : Number.parseInt(fracPart.padEnd(2, "0"), 10);
  const total = whole * 100 + frac;

  if (total > MAX_AMOUNT_CENTS) {
    throw new Error(`Amount exceeds the maximum of ${String(MAX_AMOUNT_CENTS / 100)}`);
  }
  return total as Cents;
}

export interface RoundedAmount {
  /** The value in whole cents, rounded to the nearest cent. */
  readonly cents: Cents;
  /** True when the input carried sub-cent precision that rounding discarded. */
  readonly rounded: boolean;
}

/**
 * Parse an amount that MAY carry sub-cent precision, rounding to the nearest
 * cent and reporting whether it had to. The only other decimal→cents conversion
 * besides `parseAmount`, and it exists for exactly one place: the budget-sheet
 * import edge.
 *
 * Capture must reject a third decimal loudly (`parseAmount` does). A bulk import
 * from a spreadsheet — where a formula can leave `1280.2325` in a cell — instead
 * rounds it and *counts* the rounding, so the user is told "N amounts were
 * rounded" rather than having the whole file refused over a fraction of a cent
 * (INV-7). `parseAmount` is left untouched.
 *
 * Digits are parsed as integers and combined, never `parseFloat(x) * 100`, so
 * the conversion is exact and the only imprecision is the deliberate, reported
 * rounding (INV-1).
 */
export function parseAmountRounding(input: string): RoundedAmount {
  const cleaned = input.trim().replace(/[$\s,]/g, "");

  if (cleaned === "") {
    throw new Error("Amount is empty");
  }
  if (!/^\d*\.?\d*$/.test(cleaned) || cleaned === ".") {
    throw new Error(`Not a valid amount: ${input}`);
  }

  const dot = cleaned.indexOf(".");
  const wholePart = dot === -1 ? cleaned : cleaned.slice(0, dot);
  const fracPart = dot === -1 ? "" : cleaned.slice(dot + 1);

  const whole = wholePart === "" ? 0 : Number.parseInt(wholePart, 10);
  const frac = Number.parseInt(fracPart.slice(0, 2).padEnd(2, "0"), 10);

  // Round half-up on the third decimal. Any digit beyond the third cannot change
  // a half-up decision, so consulting the single digit is exact.
  const thirdDigit = fracPart.length > 2 ? Number.parseInt(fracPart.charAt(2), 10) : 0;
  const roundUp = thirdDigit >= 5 ? 1 : 0;

  // "Rounded" means value was actually discarded. A trailing ".2300" lost
  // nothing and must not be counted, or the report cries wolf.
  const rounded = fracPart.length > 2 && /[1-9]/.test(fracPart.slice(2));

  const total = whole * 100 + frac + roundUp;
  if (total > MAX_AMOUNT_CENTS) {
    throw new Error(`Amount exceeds the maximum of ${String(MAX_AMOUNT_CENTS / 100)}`);
  }
  return { cents: total as Cents, rounded };
}

/** Parse without throwing — for live input where a partial value is expected. */
export function tryParseAmount(input: string): Cents | null {
  try {
    return parseAmount(input);
  } catch {
    // Deliberate: the caller asked for a non-throwing parse, and `null` is the
    // reported failure. This is not a swallowed error (INV-7) — it is the return value.
    return null;
  }
}

export function addCents(a: Cents, b: Cents): Cents {
  return (a + b) as Cents;
}

export function subCents(a: Cents, b: Cents): Cents {
  return (a - b) as Cents;
}

/**
 * Negate. Exists because the `Cents` brand makes `-value` a type error — which
 * is the brand doing its job: unary minus on money should go through the money
 * module, not be scattered through view code as a cast.
 */
export function negate(value: Cents): Cents {
  return -(value as number) as Cents;
}

/** Magnitude, for rendering an over-budget figure alongside its own sign. */
export function absCents(value: Cents): Cents {
  return Math.abs(value) as Cents;
}

export function sumCents(values: readonly Cents[]): Cents {
  let total = 0;
  for (const v of values) total += v;
  return total as Cents;
}

/**
 * Ratio of `part` to `whole`, as a float in 0..∞. For display only — never feed
 * the result back into money arithmetic.
 */
export function ratio(part: Cents, whole: Cents): number {
  if (whole === 0) return 0;
  return part / whole;
}

/**
 * Format for display. The only cents→currency-string conversion in the app, and
 * the only one a user reads. `toInputString` below is its machine-readable pair,
 * for editable fields, search and CSV columns.
 *
 * Both keep two decimal places for every value, always. There used to be a
 * `formatMoneyShort` that dropped them "for dense rows where the decimals are
 * noise", and it did not merely hide a trailing `.00` — it rounded to the nearest
 * dollar, so $12.50 read "$13" and $0.40 read "$0". A ledger row that disagrees
 * with the total above it is the one thing this app cannot afford to be, so there
 * is no cents-dropping formatter and there should not be one again.
 *
 * Negative values render with a true minus sign, not a hyphen.
 */
export function formatMoney(value: Cents, currency = "SGD", locale = "en-SG"): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(value / 100)
    .replace(/^-/, "−");
}

/** The digits-only form used by the numpad, e.g. `1234` → `"12.34"`. */
export function toInputString(value: Cents): string {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const whole = Math.floor(abs / 100);
  const frac = abs % 100;
  return `${sign}${String(whole)}.${String(frac).padStart(2, "0")}`;
}

/** Offerable ledger currencies have both frozen ISO and runtime two-decimal precision. */
export function isOfferableLedgerCurrency(code: string): boolean {
  return isCurrencyCode(code) && exponentOf(code) === 2 && new Intl.NumberFormat("en", { style: "currency", currency: code }).resolvedOptions().maximumFractionDigits === 2;
}

/** Always retain the current profile value so the picker can explain how to replace it. */
export function currencyOptions(current: string): string[] {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  const codes = supported ? supported("currency") : [];
  return [...new Set([current, ...codes.filter(isOfferableLedgerCurrency)])];
}

export function ledgerCurrencyOptionLabel(code: string): string {
  return isOfferableLedgerCurrency(code) ? code : `${code} (no longer offered — choose another)`;
}
